import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, CardHeader, CardTitle, Input, DatePicker } from '@monigo/ui';
import { callFunction } from '../../lib/api/client.js';
import { qk } from '../../lib/api/keys.js';
import { env } from '../../lib/env.js';
import { LoadingScreen } from '../../components/Status/LoadingScreen.js';
import { ErrorState } from '../../components/Status/ErrorState.js';
import { EmptyState } from '../../components/Status/EmptyState.js';
import { ResponsiveTable } from '../../components/Table/ResponsiveTable.js';
import { supabase } from '../../lib/supabase.js';

interface AuditRow {
  id: string;
  actor_type: string;
  actor_id: string | null;
  actor_display_name: string | null;
  action: string;
  entity_type: string;
  entity_id: string | null;
  ip_address: string | null;
  created_at: string;
}

export function AuditLogPage() {
  const { t, i18n } = useTranslation();
  const [filters, setFilters] = useState({ action: '', entity_type: '', since: '', until: '' });

  const auditQ = useQuery({
    queryKey: qk.audit(filters),
    queryFn: () =>
      callFunction<{ items: AuditRow[]; total: number }>('admin-audit-list', {
        query: {
          action: filters.action || undefined,
          entity_type: filters.entity_type || undefined,
          since: filters.since || undefined,
          until: filters.until || undefined,
        },
      }),
  });

  async function exportCsv() {
    const { data: sess } = await supabase.auth.getSession();
    const url = new URL(`${env.supabaseUrl}/functions/v1/admin-audit-export`);
    if (filters.since) url.searchParams.set('since', filters.since);
    if (filters.until) url.searchParams.set('until', filters.until);
    const res = await fetch(url, {
      headers: {
        apikey: env.supabaseAnonKey,
        authorization: `Bearer ${sess.session?.access_token ?? env.supabaseAnonKey}`,
      },
    });
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `monigo-audit-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  }

  const items = auditQ.data?.items ?? [];

  const columns = [
    {
      key: 'when',
      label: t('audit.when'),
      render: (r: AuditRow) => (
        <span className="text-text-muted">
          {new Date(r.created_at).toLocaleString(
            i18n.language === 'fr' ? 'fr-FR' : 'en-US',
          )}
        </span>
      ),
    },
    {
      key: 'actor',
      label: t('audit.actor'),
      render: (r: AuditRow) => (
        <span>
          {r.actor_display_name
            ? `${r.actor_type} · ${r.actor_display_name}`
            : r.actor_type}
          {!r.actor_display_name && r.actor_id ? ` · ${r.actor_id.slice(0, 8)}` : ''}
        </span>
      ),
    },
    {
      key: 'action',
      label: t('audit.action'),
      render: (r: AuditRow) => <span className="font-mono text-xs">{r.action}</span>,
    },
    {
      key: 'entity',
      label: t('audit.entity'),
      render: (r: AuditRow) => (
        <span>
          {r.entity_type}
          {r.entity_id ? `:${r.entity_id.slice(0, 8)}` : ''}
        </span>
      ),
    },
    {
      key: 'ip',
      label: t('audit.ip'),
      hideOnMobile: true,
      render: (r: AuditRow) => <span className="text-text-muted">{r.ip_address ?? '—'}</span>,
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('audit.title')}</CardTitle>
      </CardHeader>
      <div className="mb-4 flex flex-col gap-3 px-5 md:flex-row md:items-end">
        <div className="flex-1 space-y-1">
          <label className="text-xs font-semibold text-text-muted md:hidden">{t('audit.filterAction')}</label>
          <Input
            placeholder={t('audit.filterAction')}
            value={filters.action}
            onChange={(e) => setFilters((f) => ({ ...f, action: e.target.value }))}
          />
        </div>
        <div className="flex-1 space-y-1">
          <label className="text-xs font-semibold text-text-muted md:hidden">{t('audit.filterEntity')}</label>
          <Input
            placeholder={t('audit.filterEntity')}
            value={filters.entity_type}
            onChange={(e) => setFilters((f) => ({ ...f, entity_type: e.target.value }))}
          />
        </div>
        <div className="flex-1 space-y-1">
          <label className="text-xs font-semibold text-text-muted md:hidden">{t('audit.since')}</label>
          <DatePicker
            value={filters.since}
            onChange={(v) => setFilters((f) => ({ ...f, since: v }))}
            placeholder={t('audit.since')}
          />
        </div>
        <div className="flex-1 space-y-1">
          <label className="text-xs font-semibold text-text-muted md:hidden">{t('audit.until')}</label>
          <DatePicker
            value={filters.until}
            onChange={(v) => setFilters((f) => ({ ...f, until: v }))}
            placeholder={t('audit.until')}
          />
        </div>
        <Button variant="secondary" onClick={exportCsv} className="mt-2 md:mt-0">
          {t('common.exportCsv')}
        </Button>
      </div>
      {auditQ.isLoading ? (
        <LoadingScreen />
      ) : auditQ.isError ? (
        <ErrorState onRetry={() => auditQ.refetch()} />
      ) : items.length === 0 ? (
        <EmptyState title={t('audit.empty')} />
      ) : (
        <div className="px-5 pb-5">
          <ResponsiveTable
            columns={columns}
            data={items}
            keyExtractor={(r) => r.id}
            mobileLabel={(r) => `${r.action} — ${r.created_at.slice(0, 10)}`}
            emptyMessage={t('audit.empty')}
          />
        </div>
      )}
    </Card>
  );
}
